// app/src/App.tsx
import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { StatusBar, Style } from "@capacitor/status-bar";
import OnboardingTour from "./components/OnboardingTour";
import { fetchTourFlag, setTourFlag } from "./api/onboarding";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ProductPage from './pages/ProductPage';
import ServiceProductsPage from './pages/ServiceProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CalendarPage from "./pages/CalendarPage";
import StatisticsPage from "./pages/StatisticsPage";
import UserPage from "./pages/UserPage";
import AddCouponsPage from "./pages/User/AddCouponsPage";
import FavoritesPage from './pages/User/FavoritesPage';
import ViewPaymentMethodsPage from './pages/User/ViewPaymentMethodsPage';
import DocumentPage from "./pages/User/DocumentPage";
import ElectronicWalletPage from "./pages/User/EWalletPage";
import RecurrentBookings from "./pages/RecurrentBookings";

import api, { restoreAuthFromStorage, clearAuth } from "./api/Client";
import ConfigurationPage from "./pages/User/ConfigurationPage";
import ChangePasswordPage from "./pages/User/ChangePasswordPage";
import GlobalBar from "./components/GlobalBar";
import ProfilePage from "./pages/User/ProfilePage";
import BackHandler from "./components/BackHandler";
import ScrollToTop from "./components/ScrollToTop";
import EditProfilePage from "./pages/User/EditProfile";
import RecoverPasswordPage from "./pages/RecoverPasswordPage";
import PublicServicePage from "./pages/PublicServicePage";
import PublicServiceProductPage from "./pages/PublicServiceProductPage";
import NotificationsPage from "./pages/NotificationsPage";
import MoveHome from "./pages/Move/MoveHome";
import EventsListPage from "./pages/Move/EventsListPage";
import EventDetailPage from "./pages/Move/EventDetailPage";
import EventCreatePage from "./pages/Move/EventCreatePage";
import RoutesListPage from "./pages/Move/RoutesListPage";
import RouteDetailPage from "./pages/Move/RouteDetailPage";
import RouteCreatePage from "./pages/Move/RouteCreatePage";
import RankingPage from "./pages/Move/RankingPage";
import MyActivityPage from "./pages/Move/MyActivityPage";
import ActivityRegisterPage from "./pages/Move/ActivityRegisterPage";
import MoveFooter from "./components/MoveFooter";
import MoveProfilePage from "./pages/Move/MoveProfilePage";
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { App as CapacitorApp } from '@capacitor/app';
import { getMinVersions } from './api/version';
import { useNotifications } from "./notifications/NotificationsContext";
import { sendBirthdayGreetingOnce } from "./notifications/birthday";
import { SecureStorage } from "./storage/SecureStorage";
import { fetchUserProfile } from "./api/User";

export default function App() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [booting, setBooting] = useState(true);
  const [initialPath, setInitialPath] = useState<string | null>(null);
  const [showTour, setShowTour] = useState(false);
  const [checkedTour, setCheckedTour] = useState(false); // para evitar doble chequeo
  const { addLocalNotification } = useNotifications();


  const [forceUpdate, setForceUpdate] = useState(false);
  const [showCompleteRegistration, setShowCompleteRegistration] = useState(false);
  const [missingReg, setMissingReg] = useState({
    dni: false,
    address: false,
    postal: false,
    dob: false,
  });
  const [regDni, setRegDni] = useState("");
  const [regAddress, setRegAddress] = useState("");
  const [regPostal, setRegPostal] = useState("");
  const [regDob, setRegDob] = useState("");
  const [regError, setRegError] = useState<string | null>(null);

  // Comparador simple de versiones X.Y.Z
  function isLowerVersion(current: string, required: string): boolean {
    const cParts = (current || '').split('.').map(n => parseInt(n, 10) || 0);
    const rParts = (required || '').split('.').map(n => parseInt(n, 10) || 0);
    const len = Math.max(cParts.length, rParts.length);
    for (let i = 0; i < len; i++) {
      const c = cParts[i] || 0;
      const r = rParts[i] || 0;
      if (c < r) return true;
      if (c > r) return false;
    }
    return false;
  }

  function validateDni(value: string): string {
    const regex = /^(\d{8}[A-Z]|X\d{7}[A-Z])$/;
    if (!regex.test(value.trim())) {
      return "DNI invalido: debe tener 8 numeros y una letra mayuscula o X + 7 numeros + letra mayuscula";
    }
    return "";
  }

  function validateCodigoPostal(value: string): string {
    const regex = /^40\d{3}$/;
    if (!regex.test(value.trim())) {
      return "El codigo postal debe pertenecer a la provincia de Segovia";
    }
    return "";
  }

  function dateInputToDdmmyyyy(value: string): string {
    if (!value) return "";
    const [y, m, d] = value.split("-");
    if (!y || !m || !d) return "";
    return `${d}${m}${y}`;
  }

  function resolveMissingRegistrationFields(currentUser: any) {
    const postAddress = currentUser?.postAddress ?? currentUser?.address ?? "";
    const dateOfBirth = currentUser?.dateOfBirth ?? currentUser?.date_of_birth ?? "";
    const dniMissing = !currentUser?.dni;
    const addressMissing = !postAddress;
    const postalMissing =
      currentUser?.postcode == null || String(currentUser.postcode).trim() === "";
    const dobMissing = !dateOfBirth;
    return {
      dni: dniMissing,
      address: addressMissing,
      postal: postalMissing,
      dob: dobMissing,
    };
  }

  useEffect(() => {
    (async () => {
      try {
        // Llama al backend para obtener las versiones minimas
        const versionInfo = await getMinVersions();
        // Obtiene la version instalada desde la app
        const appInfo = await CapacitorApp.getInfo();
        const platform = Capacitor.getPlatform();
        console.log('[UpdateCheck] versionInfo', versionInfo);
        console.log('[UpdateCheck] appInfo', appInfo, 'platform', platform);

        // Android: uso de actualizacion inmediata
        if (platform === 'android') {
          const current = parseInt(String(appInfo.build ?? appInfo.version ?? 0), 10);
          const required = typeof versionInfo.minAndroid === 'number' ? versionInfo.minAndroid : parseInt(versionInfo.minAndroid as any, 10);
          console.log('[UpdateCheck][Android] current', current, 'required', required);
          if (current < required) {
            setForceUpdate(true);
            try {
              // Lanza actualizacion obligatoria (flujo in-app update inmediato) si esta disponible
              const updateInfo = await AppUpdate.getAppUpdateInfo();
              console.log('[UpdateCheck][Android] updateAvailability', updateInfo.updateAvailability);
              if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
                await AppUpdate.performImmediateUpdate();
              }
            } catch (e) {
              console.warn('No se pudo iniciar actualizacion inmediata:', e);
            }
          }
        }
        // iOS: comparar VersionName y abrir la App Store
        if (platform === 'ios') {
          const currentName = appInfo.version ?? '';
          const requiredName = (versionInfo.minIos as any) ?? '';
          console.log('[UpdateCheck][iOS] current', currentName, 'required', requiredName);
          if (isLowerVersion(currentName, String(requiredName))) {
            setForceUpdate(true);
            const url = (versionInfo as any).storeUrlIos;
            try {
              if (url) {
                window.open(url, '_system');
              } else {
                // Llama al plugin para abrir la App Store
                await AppUpdate.openAppStore();
              }
            } catch (e) {
              console.warn('No se pudo abrir App Store:', e);
            }
          }
        }
      } catch (e) {
        console.warn('Error comprobando versiones mínimas de la app:', e);
      }
    })();
  }, []);

  const inMoveModule = pathname.startsWith("/move");
  const hideLayout = ["/", "/home", "/login", "/register", "/recover-password"].includes(pathname) || inMoveModule;
  const hideFooter = ["/", "/home", "/login", "/register", "/change-password", "/settings", "/ewallet", "/payment-methods", "/profile", "/documents", "/profile/edit", "/recover-password", "/public-services"].includes(pathname) || pathname.startsWith("/public-service/") || inMoveModule;

  // Unifica la inicialización: primero restaurar auth, luego obtener usuario y flag tour
  useEffect(() => {
    (async () => {
      try {
        const { access, user } = await restoreAuthFromStorage();
        if (access && user) {
          setUser(user);
          setInitialPath(user?.moveOnly ? "/move" : "/product");
        } else {
          setUser(null);
          setInitialPath("/home");
        }
      } catch (err) {
        setUser(null);
        setInitialPath("/home");
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) {
      setShowCompleteRegistration(false);
      return;
    }
    if (inMoveModule || user?.moveOnly) {
      setShowCompleteRegistration(false);
      return;
    }
    const missing = resolveMissingRegistrationFields(user);
    const hasMissing = Object.values(missing).some(Boolean);
    if (!hasMissing) {
      setShowCompleteRegistration(false);
      return;
    }
    setMissingReg(missing);
    setRegDni(user?.dni || "");
    setRegAddress(user?.postAddress || user?.address || "");
    setRegPostal(user?.postcode != null ? String(user.postcode) : "");
    setRegDob(user?.dateOfBirth || user?.date_of_birth || "");
    setShowCompleteRegistration(true);
  }, [user, inMoveModule]);

  // Chequea el flag del tour justo después de tener user y booting terminado
  useEffect(() => {
    if (booting || checkedTour) return;
    if (!user) {
      setShowTour(false);
      setCheckedTour(true);
      return;
    }
    (async () => {
      try {
        const alreadySeen = await fetchTourFlag();
        setShowTour(!alreadySeen);
      } catch (e) {
        console.warn("No se pudo leer tour de BD:", e);
        setShowTour(false);
      } finally {
        setCheckedTour(true);
      }
    })();
  }, [booting, user, checkedTour]);

  useEffect(() => {
    StatusBar.setOverlaysWebView({ overlay: true }); // contenido detrás
    StatusBar.setStyle({ style: Style.Dark });
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const userId =
          typeof user?.id === "number"
            ? user.id
            : typeof (user as any)?.user_id === "number"
              ? (user as any).user_id
              : null;
        if (!userId) return;
        const dateOfBirth = (user as any)?.dateOfBirth ?? (user as any)?.date_of_birth;
        const fullName = (user as any)?.fullName ?? (user as any)?.full_name ?? (user as any)?.name;
        await sendBirthdayGreetingOnce({
          userId,
          dateOfBirth,
          fullName,
          addLocalNotification,
        });
      } catch (err) {
        console.warn("No se pudo enviar la notificación de cumpleaños:", err);
      }
    })();
  }, [user, addLocalNotification]);

  async function handleCompleteRegistrationSave() {
    setRegError(null);
    const userId =
      typeof user?.id === "number"
        ? user.id
        : typeof (user as any)?.user_id === "number"
          ? (user as any).user_id
          : null;
    if (!userId) return;

    if (missingReg.dni) {
      const err = validateDni(regDni);
      if (err) {
        setRegError(err);
        return;
      }
    }
    if (missingReg.address && !regAddress.trim()) {
      setRegError("Direccion postal es obligatoria");
      return;
    }
    if (missingReg.postal) {
      const err = validateCodigoPostal(regPostal);
      if (err) {
        setRegError(err);
        return;
      }
    }
    if (missingReg.dob && !regDob) {
      setRegError("Fecha de nacimiento es obligatoria");
      return;
    }

    const body: Record<string, any> = {
      user_id: userId,
    };
    if (missingReg.dni) body.dni = regDni.trim();
    if (missingReg.address) body.direccionPostal = regAddress.trim();
    if (missingReg.postal) body.codigoPostal = regPostal.trim();
    if (missingReg.dob) body.fecha_nacimiento = dateInputToDdmmyyyy(regDob);

    try {
      await api.put("/api/mobile/update-pay-info", body);
      const updatedUser = await fetchUserProfile(userId);
      setUser(updatedUser);
      setShowCompleteRegistration(false);
      navigate("/move", { replace: true });
    } catch (err: any) {
      setRegError(err?.response?.data?.error || "Error al guardar los datos");
    }
  }

  if (booting) {
    return <div style={{ textAlign: "center", marginTop: "50%" }}>Cargando…</div>;
  }

  if (forceUpdate) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50%', padding: '1rem' }}>
        <p>Hay una nueva versión de la aplicación disponible. Debes actualizarla para continuar.</p>
      </div>
    );
  }

  return (
    <>
      <BackHandler />
      <ScrollToTop />

      {!hideLayout && <Header />}

      <Routes>
        <Route path="/" element={<Navigate to={initialPath || "/home"} replace />} />
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login onLoginSuccess={async () => {
          try {
            const storedUser = await SecureStorage.getUser();
            if (storedUser) setUser(storedUser);
            const alreadySeen = await fetchTourFlag();
            setShowTour(!alreadySeen);
            setCheckedTour(true);
          } catch (e) {
            setShowTour(false);
            setCheckedTour(true);
          }
        }} />} />
        <Route path="/recover-password" element={<RecoverPasswordPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/product/:productId" element={<ProductDetailPage />} />
        <Route path="/service-products/:serviceId" element={<ServiceProductsPage />} />
        <Route path="/public-services" element={<PublicServicePage />} />
        <Route path="/public-service/:serviceId" element={<PublicServiceProductPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/edit" element={<EditProfilePage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/recurrent-bookings" element={<RecurrentBookings />} />
        <Route path="/payment-methods" element={<ViewPaymentMethodsPage />} />
        <Route path="/documents" element={<DocumentPage />} />
        <Route path="/add-coupons" element={<AddCouponsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<ConfigurationPage
          onLogout={async () => {
            try {
              await clearAuth();
              navigate("/home", { replace: true });
            } catch (error) {
              console.error("❌ Error en logout:", error);
            }
          }}
          onDeleteAccount={async (password: string) => {
            try {
              if (!user?.email) {
                alert("No se encontró email del usuario");
                return;
              }

              // ✅ ahora se envía body con email + password
              await api.post("/api/mobile/user", {
                email: user.email,
                password,
              });

              alert("Cuenta eliminada correctamente");
              await clearAuth();
              navigate("/home", { replace: true });
            } catch (err: any) {
              console.error("❌ Error borrando cuenta:", err);
              alert(err?.response?.data?.error || "Error al eliminar la cuenta");
            }
          }}
          onChangePasswordRequested={() => {
            navigate("/change-password");
          }}
        />
        }
        />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/user" element={<UserPage />} />
        <Route path="/ewallet" element={<ElectronicWalletPage />} />
        <Route path="/move" element={<MoveHome />} />
        <Route path="/move/profile" element={<MoveProfilePage />} />
        <Route path="/move/events" element={<EventsListPage />} />
        <Route path="/move/events/new" element={<EventCreatePage />} />
        <Route path="/move/events/:id/edit" element={<EventCreatePage />} />
        <Route path="/move/events/:id" element={<EventDetailPage />} />
        <Route path="/move/routes" element={<RoutesListPage />} />
        <Route path="/move/routes/new" element={<RouteCreatePage />} />
        <Route path="/move/routes/:id" element={<RouteDetailPage />} />
        <Route path="/move/activity" element={<MyActivityPage />} />
        <Route path="/move/activity/new" element={<ActivityRegisterPage />} />
        <Route path="/move/ranking" element={<RankingPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>

      {!hideFooter && <Footer />}
      {inMoveModule && <MoveFooter />}

      <GlobalBar />

      {showCompleteRegistration && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 3000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "1.5rem",
              width: "90%",
              maxWidth: 420,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Completa tu registro</h3>
            <p style={{ marginTop: 0, fontSize: 13, color: "#666" }}>
              Para acceder a Human necesitas completar los siguientes datos. Si prefieres, puedes usar Human Move mientras tanto.
            </p>
            {missingReg.dob && (
              <>
                <label style={{ fontSize: 14 }}>Fecha de nacimiento *</label>
                <input
                  type="date"
                  value={regDob}
                  onChange={(e) => setRegDob(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    marginBottom: "0.75rem",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                  }}
                />
              </>
            )}
            {missingReg.postal && (
              <>
                <label style={{ fontSize: 14 }}>Codigo postal *</label>
                <input
                  type="text"
                  value={regPostal}
                  onChange={(e) => setRegPostal(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    marginBottom: "0.75rem",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                  }}
                />
              </>
            )}
            {missingReg.address && (
              <>
                <label style={{ fontSize: 14 }}>Direccion postal *</label>
                <input
                  type="text"
                  value={regAddress}
                  onChange={(e) => setRegAddress(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    marginBottom: "0.75rem",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                  }}
                />
              </>
            )}
            {missingReg.dni && (
              <>
                <label style={{ fontSize: 14 }}>DNI *</label>
                <input
                  type="text"
                  value={regDni}
                  onChange={(e) => setRegDni(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    marginBottom: "0.75rem",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                  }}
                />
              </>
            )}

            {regError && (
              <p style={{ color: "red", fontSize: 12, marginTop: 0 }}>
                {regError}
              </p>
            )}
            <button
              onClick={handleCompleteRegistrationSave}
              style={{
                width: "100%",
                padding: "0.75rem",
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                marginTop: "0.5rem",
              }}
            >
              Guardar y continuar
            </button>
            <button
              onClick={() => {
                setShowCompleteRegistration(false);
                navigate("/move", { replace: true });
              }}
              style={{
                width: "100%",
                padding: "0.75rem",
                background: "transparent",
                color: "#1976d2",
                border: "none",
                marginTop: "0.5rem",
              }}
            >
              Usar Human Move sin completar
            </button>
          </div>
        </div>
      )}

      {showTour && (
        <OnboardingTour
          onClose={async () => {
            try {
              await setTourFlag(true); // marcar como visto en BD
            } catch (e) {
              console.warn("No se pudo marcar tour en BD:", e);
            }
            setShowTour(false);
            setCheckedTour(true); // para evitar que vuelva a salir
          }}
        />
      )}
    </>
  );
}
